import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { auditLogsApi } from '../../services/auditLogs';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/Pagination';

function AuditLogsList() {
  const { hasRole } = useAuth();
  const [auditLogs, setAuditLogs] = useState([]);
  const [filter, setFilter] = useState({ entity_type: '', entity_id: '', action: '' });
  const { pagination, handlePageChange, updatePagination } = usePagination();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAuditLogs();
  }, [pagination.page, filter]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const response = await auditLogsApi.getAll({ 
        page: pagination.page, 
        limit: pagination.limit,
        ...filter
      });
      setAuditLogs(response.data);
      updatePagination(response.pagination);
    } catch (err) {
      setError('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
    handlePageChange(1);
  };

  const handleClear = useCallback(() => {
    setFilter({ entity_type: '', entity_id: '', action: '' });
  }, []);

  const isFilterActive = filter.entity_type || filter.entity_id || filter.action;

  const getActionLabel = (action) => {
    const labels = {
      create: 'Create',
      update: 'Update',
      delete: 'Delete',
      assign: 'Assign',
      return: 'Return',
      create_and_assign: 'Create & Assign'
    };
    return labels[action] || action;
  };

  const getEntityTypeLabel = (entityType) => {
    const labels = {
      asset: 'Asset',
      employee: 'Employee',
      client: 'Client',
      proposal: 'Proposal',
      purchase_order: 'Purchase Order'
    };
    return labels[entityType] || entityType;
  };

  if (loading) return <div className="loading">Loading audit logs...</div>;

  return (
    <div>
      <div className="page-header">
        <h2>Audit Logs</h2>
      </div>

      <div className="card">
        {error && (
          <div style={{ backgroundColor: '#ffebee', color: '#c62828', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>Entity Type</label>
              <select
                name="entity_type"
                className="form-control"
                value={filter.entity_type}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                <option value="asset">Asset</option>
                <option value="employee">Employee</option>
                <option value="client">Client</option>
                <option value="proposal">Proposal</option>
                <option value="purchase_order">Purchase Order</option>
              </select>
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>Action</label>
              <select
                name="action"
                className="form-control"
                value={filter.action}
                onChange={handleFilterChange}
              >
                <option value="">All Actions</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="assign">Assign</option>
                <option value="return">Return</option>
                <option value="create_and_assign">Create & Assign</option>
              </select>
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#666' }}>Entity ID</label>
              <input
                type="text"
                name="entity_id"
                className="form-control"
                value={filter.entity_id}
                onChange={handleFilterChange}
                placeholder="Filter by Entity ID"
              />
            </div>
            {isFilterActive && (
              <button className="btn btn-secondary btn-compact" onClick={handleClear}>
                Clear
              </button>
            )}
          </div>
        </div>

        {auditLogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', color: '#666' }}>
            No audit logs found
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Entity Type</th>
                  <th>Entity ID</th>
                  <th>Action</th>
                  <th>Changed By</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map(log => (
                  <tr key={log.id}>
                    <td>{new Date(log.created_at).toLocaleString()}</td>
                    <td>{getEntityTypeLabel(log.entity_type)}</td>
                    <td>
                      <Link to={`/audit-logs/${log.id}`} style={{ color: '#1976d2', textDecoration: 'none' }}>
                        {log.entity_id}
                      </Link>
                    </td>
                    <td>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: log.action === 'delete' ? '#ffebee' : 
                                       log.action === 'create' ? '#e8f5e9' :
                                       log.action === 'update' ? '#e3f2fd' : '#fff3e0',
                        color: log.action === 'delete' ? '#c62828' : 
                               log.action === 'create' ? '#2e7d32' :
                               log.action === 'update' ? '#1565c0' : '#e65100'
                      }}>
                        {getActionLabel(log.action)}
                      </span>
                    </td>
                    <td>{log.changed_by_name || log.changed_by || 'System'}</td>
                    <td>{log.changed_by_role || '-'}</td>
                    <td>
                      <Link 
                        to={`/audit-logs/${log.id}`} 
                        className="btn-icon btn-view"
                        title="View"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.totalPages > 1 && (
          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
}

export default AuditLogsList;
